import { PreparationData, ValidationData } from '../../../interface';
import { RootState } from '@cp/src/store';
import { useSelector } from 'react-redux';
import StatusContainer from '../DataContainers/StatusContainer';
import ProcessingDetails from '../DataContainers/ProcessingDetails';
import MockAllocationValidationTable from '../Tables/MockAllocationValidationTable';
import { downloadDocumentFromS3WithoughtPreview } from '@cp/src/utils/DocumentDownloadFromS3';
import { MockAllocationStatus, ValidationType } from '../../../types';
import { useMemo } from 'react';
import { SELF_SERVE_UI_CLICKSTREAM } from '@cp/src/service/clickStream.constant';
import FileDownloadButton from '@cp/src/components/Buttons/FileDownloadButton';

interface MockAllocationStatesProps {
  header: React.ReactNode;
  showMockAllocationDetails?: boolean;
  showTable?: boolean;
  showPreviousAllocationDetails?: boolean;
}

const DownloadButtons = ({ details }: { details: PreparationData }) => {
  const mockId =
    useSelector(
      (state: RootState) => state?.fieldAllocation?.fieldAllocationDetails?.allocationTrackerId
    ) || '';

  if (details?.preparationStatus?.value !== MockAllocationStatus.SUCCESS) return null;

  const attribute = {
    mockId: mockId
  };
  return (
    <div className="flex flex-col items-end">
      <FileDownloadButton
        label="Inhouse"
        presignedUrl={details?.presignedUrls?.inhouseLppDataExportPath}
        fileName="Mock output Inhouse"
        clickstreamEvent={SELF_SERVE_UI_CLICKSTREAM.LH_DOWNLOAD_INHOUSE_OUTPUT_CLICKED}
        attributes={attribute}
      />
      <FileDownloadButton
        label="Agency"
        presignedUrl={details?.presignedUrls?.externalLppDataExportPath}
        fileName="Mock output Agency"
        clickstreamEvent={SELF_SERVE_UI_CLICKSTREAM.LH_DOWNLOAD_AGENCY_OUTPUT_CLICKED}
        attributes={attribute}
      />
    </div>
  );
};

const MockAllocationStates = (props: MockAllocationStatesProps) => {
  const {
    header,
    showMockAllocationDetails = false,
    showTable = false,
    showPreviousAllocationDetails = false
  } = props;

  const previousAllocationData = useSelector(
    (state: RootState) => state?.fieldAllocation?.previousAllocationDetails
  );
  const currentAllocationData = useSelector(
    (state: RootState) => state?.fieldAllocation?.fieldAllocationDetails
  );

  const allocationData = showPreviousAllocationDetails
    ? previousAllocationData
    : currentAllocationData;

  const mockAllocationData = allocationData?.mockAllocationData;

  const handleOnClick = (preSignedGetUrl: string) => {
    downloadDocumentFromS3WithoughtPreview(
      preSignedGetUrl,
      'ValidationsReport',
      'File download failed'
    );
  };

  const validationItems: ValidationData[] = Object.values(mockAllocationData?.validationData || {});

  const { agencyValidationData, inhouseValidationData } = useMemo(() => {
    const agencyValidationData: ValidationData[] = [];
    const inhouseValidationData: ValidationData[] = [];
    validationItems.forEach(item => {
      if (item?.metadata?.component === ValidationType.EXTERNAL) {
        agencyValidationData.push(item);
      } else {
        inhouseValidationData.push(item);
      }
    });
    return { agencyValidationData, inhouseValidationData };
  }, [validationItems]);

  const handleAgencyValidationClick = () => {
    handleOnClick(mockAllocationData?.presignedUrls?.externalValidationPath as string);
  };

  const handleInhouseValidationClick = () => {
    handleOnClick(mockAllocationData?.presignedUrls?.inhouseValidationPath as string);
  };

  return (
    <StatusContainer
      header={header}
      detailsComponent={
        showMockAllocationDetails ? (
          <div className="flex justify-between items-end">
            <ProcessingDetails allocationDetails={allocationData} />
            <DownloadButtons details={mockAllocationData} />
          </div>
        ) : null
      }
      tableComponent={
        showTable ? (
          <div className="flex gap-5 p-1 mt-5">
            <MockAllocationValidationTable
              title={'Agency validations'}
              onClick={handleAgencyValidationClick}
              data={agencyValidationData}
              clickstreamEvent={SELF_SERVE_UI_CLICKSTREAM.LH_ERROR_REPORT_AGENCY_CLICKED}
            />

            <MockAllocationValidationTable
              title={'Inhouse validations'}
              onClick={handleInhouseValidationClick}
              data={inhouseValidationData}
              clickstreamEvent={SELF_SERVE_UI_CLICKSTREAM.LH_ERROR_REPORT_INHOUSE_CLICKED}
            />
          </div>
        ) : null
      }
    />
  );
};

export default MockAllocationStates;
